import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RunSummary, RunDetail, InvestigationRow, SourceHeaders } from '@lyra/shared';
import { apiFetch } from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { useActiveSession, useStartSession } from '@/lib/walletHooks';
import { notifyError, notifySuccess } from '@/lib/useMutationToast';

function isProcessing(run: RunDetail | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'processing';
}

export function useRuns(status?: string, limit = 50) {
  const qs = new URLSearchParams();
  if (status && status !== 'all') qs.set('status', status);
  qs.set('limit', String(limit));
  return useQuery<RunSummary[]>({
    queryKey: queryKeys.runs(status, limit),
    queryFn: () => apiFetch<RunSummary[]>(`/runs?${qs.toString()}`),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'queued' || r.status === 'processing') ? 2000 : false),
  });
}

export function useRun(id: number) {
  return useQuery<RunDetail>({
    queryKey: queryKeys.run(id),
    queryFn: () => apiFetch<RunDetail>(`/runs/${id}`),
    refetchInterval: (q) => (isProcessing(q.state.data) ? 2000 : false),
    enabled: Number.isFinite(id) && id > 0,
  });
}

function useInvalidateRuns() {
  const qc = useQueryClient();
  return (id?: number) => {
    qc.invalidateQueries({ queryKey: ['runs'] });
    if (id) qc.invalidateQueries({ queryKey: queryKeys.run(id) });
  };
}

export function useStartRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: (body: { filename: string; totalRows: number; inputRows: InvestigationRow[]; inputHeaders: SourceHeaders }) =>
      apiFetch<{ runId: number }>('/runs/start', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidate();
      // The run starts out `queued` — MaparePage renders a "Se pornește
      // instanța de calcul… 2–5 minute" panel for exactly that state, so
      // claiming the mapare already "a început" here would contradict it.
      notifySuccess('Maparea a fost creată și pornește automat.');
    },
    onError: (error) => notifyError(error, 'Maparea nu a putut începe.'),
  });
}
export function useCancelRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/runs/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'cancelled' }) }),
    onSuccess: (_d, id) => {
      invalidate(id);
      notifySuccess('Maparea a fost anulată.');
    },
    onError: (error) => notifyError(error, 'Anularea mapării a eșuat.'),
  });
}
export function useRestartRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/runs/${id}/restart`, { method: 'POST' }),
    onSuccess: (_d, id) => {
      invalidate(id);
      notifySuccess('Maparea a fost repornită.');
    },
    onError: (error) => notifyError(error, 'Repornirea mapării a eșuat.'),
  });
}

/**
 * „Repornire" that also brings up an instance. A restarted run is re-queued but
 * needs a `ready` instance to actually run — so this re-queues the run AND, if
 * no session is active yet, starts one (renting a GPU). The global SessionDrawer
 * reacts to the new session and shows it live (provisioning → active). Skips the
 * start when a session is already active/provisioning, so it never double-rents
 * or 409s. Exposes the restart mutation's `isPending`/`variables` so callers
 * keep their per-row „Se repornește…" pending state.
 */
export function useRestartRunWithInstance() {
  const restart = useRestartRun();
  const startSession = useStartSession();
  const { data: active } = useActiveSession();
  function run(id: number) {
    restart.mutate(id);
    if (!active && !startSession.isPending) {
      startSession.mutate();
    }
  }
  return { run, isPending: restart.isPending, variables: restart.variables };
}
export function useDeleteRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/runs/${id}`, { method: 'DELETE' }),
    onSuccess: (_d, id) => {
      invalidate(id);
      notifySuccess('Maparea a fost ștearsă.');
    },
    onError: (error) => notifyError(error, 'Ștergerea mapării a eșuat.'),
  });
}
export function useRenameRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: ({ id, filename }: { id: number; filename: string }) =>
      apiFetch(`/runs/${id}`, { method: 'PATCH', body: JSON.stringify({ filename }) }),
    onSuccess: (_d, { id }) => {
      invalidate(id);
      notifySuccess('Maparea a fost redenumită.');
    },
    onError: (error) => notifyError(error, 'Redenumirea mapării a eșuat.'),
  });
}
export function useFinalizeRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: ({ id, finalized }: { id: number; finalized: boolean }) =>
      apiFetch(`/runs/${id}`, { method: 'PATCH', body: JSON.stringify({ finalized }) }),
    onSuccess: (_d, { id, finalized }) => {
      invalidate(id);
      notifySuccess(finalized ? 'Maparea a fost marcată finalizată.' : 'Maparea a fost redeschisă.');
    },
    onError: (error) => notifyError(error, 'Actualizarea stării mapării a eșuat.'),
  });
}
